import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, User, ArrowRight, BookOpen, TrendingUp, Wifi, Building2 } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const BLOG_ARTICLES = [
  {
    id: "2026-uk-workspace-report",
    slug: "2026-uk-workspace-report",
    title: "The 2026 UK Workspace Report: Why the 'Third Workspace' is the Backbone of National Productivity",
    excerpt: "An in-depth analysis of how Britain's mobile workforce is reshaping commercial real estate, cafe culture, and the very definition of 'going to work' in the post-pandemic economy.",
    author: "Strider Research Team",
    date: "2026-04-15",
    readTime: "12 min read",
    category: "Industry Report",
    featured: true,
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800",
    tags: ["UK Remote Work", "Third Workspace", "Productivity", "Future of Work"]
  },
  {
    id: "digital-nomadism-vs-regional-professionalism",
    slug: "digital-nomadism-vs-regional-professionalism",
    title: "Digital Nomadism vs. Regional Professionalism: How the UK's Coffee Shop Culture is Evolving into a Business Utility",
    excerpt: "From Instagram aesthetics to genuine productivity infrastructure—we examine the transformation of Britain's high street cafes into legitimate professional environments.",
    author: "Editorial Board",
    date: "2026-04-10",
    readTime: "10 min read",
    category: "Analysis",
    featured: true,
    image: "https://images.unsplash.com/photo-1521017432531-fbd92d768814?w=800",
    tags: ["Coffee Shop Culture", "Digital Nomads", "UK Business", "Remote Work"]
  },
  {
    id: "wifi-security-power-density-guide",
    slug: "wifi-security-power-density-guide",
    title: "A Deep Dive into WiFi Security and Power Density: What Professionals Actually Need from a Workspace",
    excerpt: "Beyond 'free WiFi'—understanding the technical infrastructure that separates productive workspaces from connectivity nightmares.",
    author: "Technical Advisory Team",
    date: "2026-04-05",
    readTime: "15 min read",
    category: "Technical Guide",
    featured: true,
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800",
    tags: ["WiFi Security", "Power Infrastructure", "Technical Guide", "Workspace"]
  },
  {
    id: "best-london-zone-1-workspaces",
    slug: "best-london-zone-1-workspaces",
    title: "Mapping London's Most WiFi-Stable Work Hubs: A Zone 1 Professional's Guide",
    excerpt: "Verified connectivity data, power availability, and real-world testing from Central London's top third workspaces.",
    author: "London Desk",
    date: "2026-03-28",
    readTime: "8 min read",
    category: "City Guide",
    featured: false,
    image: "https://images.unsplash.com/photo-1486299267070-83823f5448dd?w=800",
    tags: ["London", "Zone 1", "WiFi", "City Guide"]
  },
  {
    id: "remote-work-etiquette-uk",
    slug: "remote-work-etiquette-uk",
    title: "The Unwritten Rules: Remote Work Etiquette in British Cafes and Public Spaces",
    excerpt: "Navigating the social contract between mobile professionals and hospitality venues—from purchase expectations to peak-hour courtesy.",
    author: "Community Team",
    date: "2026-03-20",
    readTime: "6 min read",
    category: "Guide",
    featured: false,
    image: "https://images.unsplash.com/photo-1453614512568-c4024d13c247?w=800",
    tags: ["Etiquette", "UK Culture", "Remote Work", "Coffee Shops"]
  }
];

export default function BlogPage() {
  const featuredArticles = BLOG_ARTICLES.filter(a => a.featured);
  const recentArticles = BLOG_ARTICLES.filter(a => !a.featured);

  return (
    <div className="min-h-screen bg-surface">
      <SEO 
        title="Strider Journal | UK Remote Work & Third Workspace Insights"
        description="Expert analysis, guides, and research on the UK's evolving remote work landscape. Discover the best third workspaces and productivity insights for British professionals."
        url="https://www.strider.work/blog"
      />
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand/10 text-brand rounded-full text-sm font-medium mb-6">
            <BookOpen className="w-4 h-4" />
            The Strider Journal
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Insights for the Mobile Professional
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Expert analysis, research, and practical guides on the UK's evolving remote work landscape. 
            Helping British professionals find their optimal third workspace.
          </p>
        </div>

        {/* Featured Articles */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-brand" />
            Featured Research & Analysis
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredArticles.map((article) => (
              <Link 
                key={article.id}
                to={`/blog/${article.slug}`}
                className="group bg-white rounded-2xl shadow-sm hover:shadow-lg transition-all overflow-hidden"
              >
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                    <span className="px-2 py-1 bg-brand/10 text-brand rounded-full text-xs font-medium">
                      {article.category}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {article.readTime}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-brand transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-gray-500">
                      <User className="w-4 h-4" />
                      {article.author}
                    </div>
                    <span className="text-brand font-medium flex items-center gap-1">
                      Read <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Recent Articles */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Recent Articles</h2>
          <div className="space-y-6">
            {recentArticles.map((article) => (
              <Link 
                key={article.id}
                to={`/blog/${article.slug}`}
                className="group flex flex-col sm:flex-row gap-6 bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all"
              >
                <div className="sm:w-48 h-32 rounded-lg overflow-hidden flex-shrink-0">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 text-sm text-gray-500 mb-2">
                    <span className="px-2 py-1 bg-gray-100 rounded-full text-xs font-medium">
                      {article.category}
                    </span>
                    <span>{article.date}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {article.readTime}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-brand transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2">
                    {article.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-br from-brand to-brand-dark rounded-2xl p-8 sm:p-12 text-white text-center">
          <Building2 className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            Find Your Perfect Third Workspace
          </h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            Discover WiFi-verified cafes, hotels, and libraries across the UK. 
            Real data from real professionals.
          </p>
          <Link 
            to="/browse"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-brand font-semibold rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Wifi className="w-5 h-5" />
            Browse Workspaces
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}
